import { useEffect, useMemo, useState } from 'react'
import { Empty, Input, Spin, Tag, Typography } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { adminApi } from '../../api'
import type { KnowledgeScope } from '../../constants/knowledgeScope'
import { scopeQuery } from '../../constants/knowledgeScope'

type Props = {
  scope: KnowledgeScope
  value?: string[]
  onChange?: (tags: string[]) => void
}

function normalizeTags(tags: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of tags) {
    const t = raw.trim()
    if (t && !seen.has(t)) {
      seen.add(t)
      out.push(t)
    }
  }
  return out
}

export function TagsBrowsePanel({ scope, value, onChange }: Props) {
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<string[]>([])
  const [keyword, setKeyword] = useState('')
  const [draft, setDraft] = useState('')
  const selected = value || []

  useEffect(() => {
    setLoading(true)
    adminApi
      .get<{ items: string[] }>('/admin/knowledge/tags', { params: scopeQuery(scope) })
      .then((r) => setOptions(r.data.items || []))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false))
  }, [scope])

  const allOptions = useMemo(() => {
    const merged = normalizeTags([...options, ...selected])
    return merged.sort((a, b) => a.localeCompare(b, 'zh-CN'))
  }, [options, selected])

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase()
    if (!kw) return allOptions
    return allOptions.filter((t) => t.toLowerCase().includes(kw))
  }, [allOptions, keyword])

  const toggleTag = (tag: string) => {
    const next = selected.includes(tag)
      ? selected.filter((t) => t !== tag)
      : normalizeTags([...selected, tag])
    onChange?.(next)
  }

  const addDraft = () => {
    const next = normalizeTags([...selected, draft])
    if (!next.length || next.length === selected.length) return
    onChange?.(next)
    setDraft('')
  }

  if (loading) {
    return <Spin size="small" />
  }

  return (
    <>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
        点击选择标签，已选 {selected.length} 个
      </Typography.Text>
      <Input
        allowClear
        size="small"
        placeholder="搜索标签"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <div style={{ marginBottom: 8 }}>
        {filtered.length ? (
          filtered.map((tag) => {
            const active = selected.includes(tag)
            return (
              <Tag
                key={tag}
                color={active ? 'blue' : 'default'}
                style={{ marginBottom: 6, cursor: 'pointer' }}
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </Tag>
            )
          })
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无标签" />
        )}
      </div>
      <Input
        size="small"
        placeholder="新标签，回车添加"
        value={draft}
        suffix={<PlusOutlined style={{ color: '#94a3b8' }} />}
        onChange={(e) => setDraft(e.target.value)}
        onPressEnter={addDraft}
        onBlur={() => draft.trim() && addDraft()}
      />
    </>
  )
}
